import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { contactName } from "@/lib/contacts";
import { useDeleteLead } from "@/lib/queries";
import type { LeadCard } from "@/lib/types";

/**
 * Delete a contact — an admin's (ADR-078).
 *
 * Core-api takes the lead off every screen and every dial list in one commit and keeps it
 * underneath: its calls stay in the reports, and a do-not-call on it still blocks the phone. The
 * dialog says exactly that, because "delete" usually means "gone", and here it does not. There is
 * no undelete in the console yet, so the button is the danger one and the reason is asked for.
 */
export function DeleteLeadDialog({
  open, onOpenChange, contact, onDeleted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: LeadCard;
  /** After the delete: close whatever showed the contact, which is now a 404. */
  onDeleted: () => void;
}) {
  const toast = useToast();
  const remove = useDeleteLead();
  const [reason, setReason] = React.useState("");
  const [problem, setProblem] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!open) return;
    setReason("");
    setProblem(null);
  }, [open]);
  const name = contactName(contact) ?? contact.phone;
  const submit = () => {
    remove.mutate(
      { leadId: contact.id, reason: reason.trim() || null },
      {
        onSuccess: () => {
          onOpenChange(false);
          toast.success(`${name} deleted`, "Off every list and screen. Their calls stay in the reports.");
          onDeleted();
        },
        onError: (e) => setProblem(e instanceof ApiError ? e.message : "Could not delete. Please try again."),
      },
    );
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Delete {name}?</DialogTitle>
          <DialogDescription>
            They leave every dial list, the board and the contacts list at once. Their calls, notes and
            history are kept for the reports, and a do-not-call on them still blocks the number.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Reason" htmlFor="delete-reason" hint="Kept with the deletion and on the record's history.">
            <Input
              id="delete-reason"
              value={reason}
              maxLength={500}
              placeholder="e.g. a test record, or a duplicate of another contact"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setReason(e.target.value);
                setProblem(null);
              }}
            />
          </Field>
          {problem && <Callout tone="danger">{problem}</Callout>}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="danger" loading={remove.isPending} onClick={submit}>Delete contact</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
